import React, { useMemo, useState } from 'react';
import DataTable from 'react-data-table-component';
import '../../../libs/css/dataTable.css';
import './hourlyRevenue.css';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

const REVENUE_COLOR = '#404472';
const COUNT_COLOR = '#2867b2';

const formatCurrency = (value) => `₦${Number(value || 0).toLocaleString()}`;
const formatNumber = (value) => Number(value || 0).toLocaleString();

const hourLabelFromTime = (time) => {
    const parts = String(time).split(' ');
    return parts[1] ? parts[1].slice(0, 5) : time;
};

function ChartSkeleton() {
    return (
        <div>
            {[1, 2].map((n) => (
                <div className="card mb-4" key={n}>
                    <div className="card-body">
                        <div className="hourly-skeleton-block hourly-skeleton-title" />
                        <div className="hourly-skeleton-block hourly-skeleton-chart" />
                    </div>
                </div>
            ))}
        </div>
    );
}

function HourlyRevenueCharts({ isLoading, isFade, hasFetchedOnce, error, hourlyRevenue, hourlyCount, selectedDateStr }) {
    const [viewMode, setViewMode] = useState('chart');

    const combinedData = useMemo(() => {
        const timeMap = new Map();
        (hourlyRevenue || []).forEach((row) => {
            timeMap.set(row.time, {
                time: row.time,
                hourLabel: hourLabelFromTime(row.time),
                revenue: parseFloat(row.chargeAmount) || 0,
                count: 0,
            });
        });
        (hourlyCount || []).forEach((row) => {
            const count = Number(row.subscriber_count) || 0;
            const existing = timeMap.get(row.time);
            if (existing) {
                existing.count = count;
            } else {
                timeMap.set(row.time, {
                    time: row.time,
                    hourLabel: hourLabelFromTime(row.time),
                    revenue: 0,
                    count,
                });
            }
        });
        return Array.from(timeMap.values()).sort((a, b) => a.time.localeCompare(b.time));
    }, [hourlyRevenue, hourlyCount]);

    const totalRevenue = useMemo(() => combinedData.reduce((sum, row) => sum + row.revenue, 0), [combinedData]);
    const totalCount = useMemo(() => combinedData.reduce((sum, row) => sum + row.count, 0), [combinedData]);

    const todayStr = (() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    })();
    const isToday = selectedDateStr === todayStr;
    const latestHourLabel = combinedData.length ? combinedData[combinedData.length - 1].hourLabel : null;

    if (isLoading) {
        return <ChartSkeleton />;
    }

    if (error) {
        return (
            <div className="alert alert-danger" role="alert">
                {error}
            </div>
        );
    }

    if (!hasFetchedOnce) {
        return null;
    }

    if (combinedData.length === 0) {
        return (
            <div className="card">
                <div className="card-body text-center p-5">
                    <h5>No data yet for this hour range</h5>
                    <p className="mb-0">
                        {isToday
                            ? 'No charging activity has been recorded yet today. Check back after some hours have elapsed.'
                            : `No charging activity was recorded for ${selectedDateStr}.`}
                    </p>
                </div>
            </div>
        );
    }

    const chartWidth = Math.max(600, combinedData.length * 45);

    return (
        <div style={{ opacity: isFade ? 0.3 : 1 }}>
            {isToday && latestHourLabel && (
                <div className="alert alert-info" role="status">
                    Showing data through {latestHourLabel} — today is still in progress.
                </div>
            )}

            <div className="row mt-3 mb-3">
                <div className="col-md-6">
                    <div className="card bg-primary text-white">
                        <div className="card-body">
                            <h5 className="card-title">Total Revenue</h5>
                            <h3>{formatCurrency(totalRevenue)}</h3>
                        </div>
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="card bg-success text-white">
                        <div className="card-body">
                            <h5 className="card-title">Total Subscriber Count</h5>
                            <h3>{formatNumber(totalCount)}</h3>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row mb-3">
                <div className="col-md-12 text-right hourly-view-toggle" role="group" aria-label="Toggle between chart and table view">
                    <button
                        type="button"
                        className={`btn btn-outline-secondary mr-2 ${viewMode === 'chart' ? 'active' : ''}`}
                        onClick={() => setViewMode('chart')}
                        aria-pressed={viewMode === 'chart'}
                        style={{ marginRight: '8px' }}
                    >
                        Chart View
                    </button>
                    <button
                        type="button"
                        className={`btn btn-outline-secondary ${viewMode === 'table' ? 'active' : ''}`}
                        onClick={() => setViewMode('table')}
                        aria-pressed={viewMode === 'table'}
                    >
                        Table View
                    </button>
                </div>
            </div>

            {viewMode === 'table' ? (
                <div className="card mb-4">
                    <div className="card-body">
                        <DataTable
                            className="services_datatable users"
                            columns={[
                                { name: 'Hour', selector: row => row.hourLabel, sortable: true },
                                { name: 'Revenue', selector: row => formatCurrency(row.revenue), sortable: true, sortFunction: (a, b) => a.revenue - b.revenue },
                                { name: 'Subscriber Count', selector: row => formatNumber(row.count), sortable: true, sortFunction: (a, b) => a.count - b.count },
                            ]}
                            data={combinedData}
                            highlightOnHover
                            striped
                            responsive
                        />
                    </div>
                </div>
            ) : (
                <>
                    <div className="card mb-4">
                        <div className="card-header">
                            <h5>Hourly Revenue</h5>
                        </div>
                        <div className="card-body">
                            <div className="hourly-scroll-wrapper">
                                <div style={{ width: '100%', minWidth: chartWidth, height: 320 }}>
                                    <ResponsiveContainer>
                                        <LineChart data={combinedData} syncId="hourly-summary" margin={{ top: 5, right: 30, left: 20, bottom: 20 }}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="hourLabel" interval={0} tick={{ fontSize: 11 }} />
                                            <YAxis tickFormatter={formatCurrency} width={90} />
                                            <Tooltip formatter={(value) => [formatCurrency(value), 'Revenue']} />
                                            <Legend />
                                            <Line type="monotone" dataKey="revenue" name="Revenue" stroke={REVENUE_COLOR} strokeWidth={2} dot={false} activeDot={{ r: 6 }} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="card mb-4">
                        <div className="card-header">
                            <h5>Hourly Subscriber Count</h5>
                        </div>
                        <div className="card-body">
                            <div className="hourly-scroll-wrapper">
                                <div style={{ width: '100%', minWidth: chartWidth, height: 320 }}>
                                    <ResponsiveContainer>
                                        <LineChart data={combinedData} syncId="hourly-summary" margin={{ top: 5, right: 30, left: 20, bottom: 20 }}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="hourLabel" interval={0} tick={{ fontSize: 11 }} />
                                            <YAxis tickFormatter={formatNumber} width={90} />
                                            <Tooltip formatter={(value) => [formatNumber(value), 'Subscriber Count']} />
                                            <Legend />
                                            <Line type="monotone" dataKey="count" name="Subscriber Count" stroke={COUNT_COLOR} strokeWidth={2} dot={false} activeDot={{ r: 6 }} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

export default HourlyRevenueCharts;
